import React from 'react';
import { motion } from 'framer-motion';
import { Turn } from '../../types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface TurnBlockProps {
  turn: Turn;
  side: 'A' | 'B';
}

const TurnBlock: React.FC<TurnBlockProps> = ({ turn, side }) => {
  const sideStyles = {
    A: {
      rule: 'bg-steel-blue',
      border: 'border-steel-blue/30',
      bg: 'bg-steel-blue/5',
      accent: 'text-steel-blue',
      label: 'For',
    },
    B: {
      rule: 'bg-vermilion',
      border: 'border-vermilion/30',
      bg: 'bg-vermilion/5',
      accent: 'text-vermilion',
      label: 'Against',
    },
  };

  // For blockquotes in rebuttals, use the opposing side's colors
  const opposingSideStyles = side === 'A' ? sideStyles.B : sideStyles.A;
  const s = sideStyles[side];

  const typeLabels = {
    research: 'Research & Evidence',
    opening: 'Opening Statement',
    rebuttal: 'Rebuttal',
    closing: 'Closing Argument',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="card-frame relative mb-6 overflow-hidden"
    >
      {/* Side rule */}
      <span className={`absolute inset-y-0 left-0 w-1 ${s.rule}`} aria-hidden />

      {/* Header */}
      <div className="flex items-center justify-between gap-3 border-b border-black/[0.06] py-4 pl-6 pr-5">
        <div className="flex min-w-0 items-center gap-3">
          <span className={`font-mono text-[11px] font-medium uppercase tracking-wider ${s.accent}`}>
            {s.label}
          </span>
          <span className="h-3.5 w-px bg-hairline" aria-hidden />
          <h4 className="truncate font-sans text-[15px] font-semibold text-charcoal">
            {typeLabels[turn.type]}
          </h4>
        </div>
        <span className="flex-shrink-0 font-mono text-[10px] uppercase tracking-wider text-ink-muted">
          {turn.round && `R${turn.round} · `}
          {new Date(turn.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>

      {/* Content */}
      <div className="h-[600px] overflow-hidden py-5 pl-6 pr-4">
        {turn.isTyping ? (
          <div className="flex h-full items-center justify-center gap-3 py-8">
            <span className="text-sm text-ink-muted">AI is crafting its response</span>
            <div className="flex gap-1">
              <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-charcoal/50" />
              <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-charcoal/50 [animation-delay:0.2s]" />
              <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-charcoal/50 [animation-delay:0.4s]" />
            </div>
          </div>
        ) : (
          <div className="h-full max-w-none">
            <div
              className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent hover:scrollbar-thumb-gray-400 h-full overflow-y-auto pr-3 text-charcoal/85"
              style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
            >
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  p: ({ children }) => (
                    <p className="mb-5 text-[15px] leading-relaxed text-charcoal/85">{children}</p>
                  ),
                  blockquote: ({ children }) => {
                    // Use opposing side's colors for blockquotes in rebuttals
                    const quoteStyles = turn.type === 'rebuttal' ? opposingSideStyles : s;
                    return (
                      <blockquote
                        className={`my-5 border-l-2 ${quoteStyles.border.replace('/30', '')} ${quoteStyles.bg} rounded-r-lg py-3 pl-5 font-serif text-[15px] italic text-charcoal/75`}
                      >
                        {children}
                      </blockquote>
                    );
                  },
                  ul: ({ children }) => (
                    <ul className="my-4 ml-6 list-outside list-disc space-y-2 text-[15px]">{children}</ul>
                  ),
                  ol: ({ children }) => (
                    <ol className="my-4 ml-6 list-outside list-decimal space-y-2 text-[15px]">{children}</ol>
                  ),
                  li: ({ children }) => <li className="pl-1 leading-relaxed">{children}</li>,
                  strong: ({ children }) => (
                    <strong className="font-semibold text-charcoal">{children}</strong>
                  ),
                  em: ({ children }) => <em className="italic text-charcoal/75">{children}</em>,
                  code: ({ children }) => (
                    <code className="rounded bg-black/[0.05] px-1.5 py-0.5 font-mono text-[13px]">
                      {children}
                    </code>
                  ),
                  h3: ({ children }) => (
                    <h3 className="mb-3 mt-6 font-sans text-lg font-semibold text-charcoal">{children}</h3>
                  ),
                  h4: ({ children }) => (
                    <h4 className="mb-2 mt-4 font-sans text-base font-semibold text-charcoal">{children}</h4>
                  ),
                }}
              >
                {turn.content}
              </ReactMarkdown>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default TurnBlock;
